// Engagement service for handling likes, dislikes, and shares
// Uses server-side API endpoints instead of direct Supabase calls to avoid CORS issues
import { Engagement, UserVote, ShareEvent } from './supabaseSchema';
import { RoastData } from './analyzerService';
import { env } from '../env';

// API base URL
const API_BASE_URL = `${env.API_BASE_URL}/api`;

// Generate a simple user ID for anonymous users
// In a real app, you'd use authentication or a more robust method
const getUserId = (): string => {
  let userId = localStorage.getItem('amazon_roaster_user_id');
  if (!userId) {
    userId = 'user_' + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('amazon_roaster_user_id', userId);
  }
  return userId;
};

// Initialize or get engagement data for a roast
export const getEngagement = async (roastId: string): Promise<Engagement> => {
  try {
    console.log('Getting engagement for roast ID:', roastId);
    
    // Get engagement data from the server API
    const response = await fetch(`${API_BASE_URL}/engagement/${roastId}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get engagement: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('Engagement data from API:', data);
    
    return data as Engagement;
  } catch (error) {
    console.error('Error getting engagement data:', error);
    // Return a default engagement object if there's an error
    return {
      id: '',
      roast_id: roastId,
      likes: 0,
      dislikes: 0,
      shares: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }
};

// Get the current user's vote for a roast
export const getUserVote = async (roastId: string): Promise<'like' | 'dislike' | null> => {
  try {
    const userId = getUserId();
    
    // Get user vote from the server API
    const response = await fetch(`${API_BASE_URL}/vote/${roastId}/${userId}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get user vote: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.voteType;
  } catch (error) {
    console.error('Error getting user vote:', error);
    return null;
  }
};

// Record a vote (like or dislike)
export const recordVote = async (
  roastId: string, 
  voteType: 'like' | 'dislike' | null
): Promise<{ engagement: Engagement; userVote: 'like' | 'dislike' | null }> => {
  try {
    const userId = getUserId();
    
    // Send vote data to the server API
    const response = await fetch(`${API_BASE_URL}/vote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        roastId,
        userId,
        voteType
      })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to record vote: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('Vote recorded:', data);
    
    return {
      engagement: data.engagement,
      userVote: data.userVote
    };
  } catch (shareError) {
    console.error('Error recording vote:', shareError);
    return { 
      engagement: await getEngagement(roastId), 
      userVote: await getUserVote(roastId) 
    };
  }
};

// Record a share event
export const recordShare = async (
  roastId: string, 
  platform: string
): Promise<Engagement> => {
  try {
    // Get the user ID
    const userId = getUserId();
    
    // Send share data to the server API
    const response = await fetch(`${API_BASE_URL}/share`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        roastId,
        platform,
        userId
      })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to record share: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('Share recorded:', data);
    
    return data as Engagement;
  } catch (shareError) {
    console.error('Error recording share:', shareError);
    return getEngagement(roastId);
  }
};

// Function to share a roast
export async function shareRoast(roastData: RoastData): Promise<{ shareId: string }> {
  try {
    const response = await fetch(`${API_BASE_URL}/share`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(roastData),
    });

    if (!response.ok) {
      throw new Error(`Share request failed with status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error in shareRoast:', error);
    throw error;
  }
}

// Function to get a shared roast by ID
export async function getSharedRoast(shareId: string): Promise<RoastData> {
  try {
    const response = await fetch(`${API_BASE_URL}/share/${shareId}`);

    if (!response.ok) {
      throw new Error(`Get shared roast request failed with status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error in getSharedRoast:', error);
    throw error;
  }
}

// Export a dummy function to ensure this file is treated as a module
export const dummy = {};
